/**
 * View transform: pan and zoom, as pure math over a { x, y, scale } object.
 *
 * The canvas keeps all wall/stroke geometry in MODEL space and applies pan/zoom
 * as a single transform on a wrapping SVG group — never by moving the geometry
 * itself. That separation is what lets a stylus position map back to the exact
 * model point under the tip at any zoom level, and it keeps this math pure and
 * testable with no DOM.
 *
 *   model point  --( * scale, + translate )-->  screen point
 *   screen point --( - translate, / scale )-->  model point
 *
 * Ported from reference/bluetooth-room-sketch-prototype/index.html.
 */

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 12;

/** A fresh identity view. */
export function identityView() {
  return { x: 0, y: 0, scale: 1 };
}

function clampScale(scale) {
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
}

/** model -> screen */
export function toScreen(view, modelPoint) {
  return {
    x: modelPoint.x * view.scale + view.x,
    y: modelPoint.y * view.scale + view.y,
  };
}

/** screen -> model */
export function toModel(view, screenPoint) {
  return {
    x: (screenPoint.x - view.x) / view.scale,
    y: (screenPoint.y - view.y) / view.scale,
  };
}

/**
 * Returns a new view zoomed to `newScale` while keeping the model point that is
 * currently under (atX, atY) visually fixed there. This is what makes
 * scroll-wheel and pinch zoom feel anchored rather than drifting.
 */
export function zoomAt(view, newScale, atX, atY) {
  const scale = clampScale(newScale);
  const model = toModel(view, { x: atX, y: atY });
  return {
    scale,
    x: atX - model.x * scale,
    y: atY - model.y * scale,
  };
}

/** Returns a new view panned by a screen-space delta. */
export function panBy(view, dx, dy) {
  return { ...view, x: view.x + dx, y: view.y + dy };
}

/**
 * Returns a view that fits `points` (model space) within a viewport of
 * `viewW` x `viewH`, centered, with padding. Falls back to identity when there
 * is nothing to fit.
 */
export function fitPoints(points, viewW, viewH, options = {}) {
  const pad = options.pad ?? 70;
  if (!points || points.length === 0) return identityView();

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const w = Math.max(maxX - minX, 1);
  const h = Math.max(maxY - minY, 1);
  const scale = clampScale(Math.min((viewW - pad * 2) / w, (viewH - pad * 2) / h));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  return {
    scale,
    x: viewW / 2 - cx * scale,
    y: viewH / 2 - cy * scale,
  };
}

/** The CSS transform string for the viewport group. */
export function transformString(view) {
  return `translate(${view.x.toFixed(2)},${view.y.toFixed(2)}) scale(${view.scale.toFixed(4)})`;
}
